import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { TabComponent } from "@/components/tabs/TabComponent";
import { PermissionCode } from "@/constants/permissions";
import { useRoles } from "@/hooks/useRoles";
import { Button, Flex, Text, useDisclosure } from "@chakra-ui/react";
import { Trash, Fuel, Plus, Droplet } from "lucide-react";
import { lazy, Suspense, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FieldValues, useForm } from "react-hook-form";

import { AppColors } from "@/styles/colors";
import { AppRoutes } from "@/constants/routes";
import {
  clearFiltersHandler,
  fetchProductsSectionHandler,
  handleDeleteProductsHandler,
  submitFiltersProductsSection,
} from "./schema/handler-functions";
import { ProductEntity } from "./schema/products.schema";
import { FertilizantesTable } from "./sections/FertilizantesTable";
import { CombustiblesTable } from "./sections/CombustiblesTable";
import { ProductsFilter } from "@/components/common/section-components/filters/ProductsFilter";
import { usePolling } from "@/hooks/usePolling";

export const ProductsPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const navigate = useNavigate();

  const { state } = useLocation();
  const validTabs = new Set(["Fertilizantes", "Combustibles"]);
  const initialTab = validTabs.has(state?.tab) ? state.tab : "Fertilizantes";
  const [currentTab, setCurrentTab] = useState<string>(initialTab);

  useEffect(() => {
    if (state?.tab && validTabs.has(state.tab) && state.tab !== currentTab) {
      setCurrentTab(state.tab);
    }
  }, [state?.tab]);

  const [loadingQuery, setLoadingQuery] = useState<boolean>(false);

  const [fertilizantes, setFertilizantes] = useState<
    ProductEntity[] | undefined
  >(undefined);

  const [combustibles, setCombustibles] = useState<
    ProductEntity[] | undefined
  >();

  const [currentFilters, setCurrentFilters] = useState<any>();

  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);

  const { control, handleSubmit, getValues, setValue } = useForm();
  const [openFilters, setOpenFilters] = useState<boolean>(false);

  const { fetchPermissions, userPermissions } = useRoles();

  usePolling({
    callback: () => {
      if (currentFilters && Object.keys(currentFilters).length > 0) {
        fetchSection(currentTab, currentFilters, false);
      } else {
        fetchSection(currentTab, {}, false);
      }
    },
    isActive,
    delay: 15000,
  });  

  const fetchSection = async (
    section: string,
    filters?: any,
    showLoad = true,
  ) => {
    await fetchProductsSectionHandler({
      setLoadingQuery,
      setFertilizantes,
      setCombustibles,
      filters,
      section,
      showLoad,
      navigate,
      fetchPermissions
    });
  };

  useEffect(() => {
    if (!isActive) return;

    setFiltersApplied(false);
    setOpenFilters(false);
    setCurrentFilters({});
    setFertilizantesSelection([]);
    setCombustiblesSelection([]);
    setValue("name", "");

    fetchSection(currentTab);
  }, [currentTab, isActive]);

  const clearFilters = async () => {
    await clearFiltersHandler({
      getValues,
      setFiltersApplied,
      setValue,
      submitFilters,
      setCurrentFilters,
    });
  };

  const submitFilters = async (event: FieldValues) => {
    await submitFiltersProductsSection({
      event,
      fetchSection,
      setFiltersApplied,
      setOpenFilters,
      currentSection: currentTab,
      setCurrentFilters,
    });
  };

  const [fertilizantesSelection, setFertilizantesSelection] = useState<
    string[]
  >([]);
  const [combustiblesSelection, setCombustiblesSelection] = useState<string[]>(
    [],
  );

  const handleAdd = () => {
    navigate(AppRoutes.PRODUCTS_CREATION);
  };

  const { open, setOpen } = useDisclosure();
  const [loadingDeleteProducts, setLoadingDeleteProducts] =
    useState<boolean>(false);

  const getSelectionToSend = () => {
    switch (currentTab) {
      case "Fertilizantes":
        return fertilizantesSelection;
      case "Combustibles":
        return combustiblesSelection;
      default:
        return [];
    }
  };

  const handleDelete = async () => {
    handleDeleteProductsHandler({
      section: currentTab,
      selection: getSelectionToSend(),
      setLoadingDeleteProducts,
      setOpenConfirm: setOpen,
      fetchSection,
      setSelection:
        currentTab === "Fertilizantes"
          ? setFertilizantesSelection
          : setCombustiblesSelection,
    });
  };

  const hasWriteAccess = Boolean(
    userPermissions?.some(
      (p) => p.codigo === PermissionCode.PRODUCTS && p.tipoPermiso === "W",
    ),
  );

  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      gap="30px"
      data-testid="products-page"
    >
      <SectionHeader
        title="Maestro de productos"
        showButtons={hasWriteAccess}
        aditionalButtons={
          hasWriteAccess ? (
            <Flex gap="16px">
              <Button
                bgColor={AppColors.GREEN_PRIMARY}
                paddingX="10px"
                disabled={loadingDeleteProducts}
                onClick={handleAdd}
              >
                <Plus />
                <Text>Agregar</Text>
              </Button>
              <Button
                paddingX="10px"
                onClick={() => setOpen(true)}
                disabled={
                  loadingDeleteProducts ||
                  (!fertilizantesSelection?.length &&
                    !combustiblesSelection.length)
                }
              >
                <Trash />
                Eliminar
              </Button>
            </Flex>
          ) : undefined
        }
      />

      <TabComponent
        onValueChange={(value) => setCurrentTab(value.value)}
        value={currentTab}
        tabs={[
          {
            label: "Fertilizantes",
            icon: <Droplet />,
            value: "Fertilizantes",
            content: (
              <FertilizantesTable
                loadingQuery={loadingQuery}
                noActions={!hasWriteAccess}
                noSelection={!hasWriteAccess}
                setFertilizantesSelection={setFertilizantesSelection}
                sectionData={fertilizantes}
                fertilizantesSelection={fertilizantesSelection}
              />
            ),
          },
          {
            label: "Combustibles",
            icon: <Fuel />,
            value: "Combustibles",
            content: (
              <CombustiblesTable
                loadingQuery={loadingQuery}
                noActions={!hasWriteAccess}
                noSelection={!hasWriteAccess}
                setCombustiblesSelection={setCombustiblesSelection}
                sectionData={combustibles}
                combustiblesSelection={combustiblesSelection}
              />
            ),
          },
        ]}
        additionalButtons={
          <ProductsFilter
            filtersApplied={filtersApplied}
            openFilters={openFilters}
            control={control}
            setOpenFilters={setOpenFilters}
            clearFilters={clearFilters}
            handleSubmit={handleSubmit}
            submitFilters={submitFilters}
          />
        }
      />

      <Suspense>
        <ConfirmModalDynamic
          openConfirm={open}
          setOpenConfirm={setOpen}
          title={`Eliminar ${currentTab.toLocaleLowerCase()}`}
          subtitle={`¿Desea eliminar los ${currentTab.toLocaleLowerCase()} (${getSelectionToSend().length}) seleccionados? No podrá deshacer esta acción después.`}
          loadingAccept={loadingDeleteProducts}
          handleAccept={handleDelete}
        />
      </Suspense>
    </Flex>
  );
};

const ConfirmModalDynamic = lazy(() =>
  import("@/components/modal/ConfirmModal").then((el) => ({
    default: el["ConfirmModal"],
  })),
);

